/**
 * Order activity tabs — history, outbound packages, return packages,
 * escalations, and email history. Each table is a react-admin <Datagrid>
 * (via the shared ActivityGrid) with <TextField>/<DateField>/<FunctionField>
 * columns instead of a hand-built MUI table.
 */
import { DateField, FunctionField, TextField } from 'react-admin';
import { Link } from '@mui/material';

import { ActivityGrid, ActivityTabs, type ActivityTab } from '@src/components/indexPage';

import { formatMoney } from './format';
import { present } from './types';
import type { Escalation, OrderEmail, OrderRecord, Ypackage } from './types';

const dims = (pkg: Ypackage): string | null =>
  pkg.length && pkg.width && pkg.height ? `${pkg.length}″ × ${pkg.width}″ × ${pkg.height}″` : null;

const trackingCell = (pkg: Ypackage) => {
  if (!pkg.trackingNumber) return '—';
  return pkg.trackingUrl ?
    <Link href={pkg.trackingUrl} target="_blank" rel="noopener">{pkg.trackingNumber}</Link> :
    pkg.trackingNumber;
};

const PackageGrid = ({
  packages,
  currency,
  isReturn,
}: {
  packages: Ypackage[];
  currency: string | null | undefined;
  isReturn: boolean;
}) => (
  <ActivityGrid rows={packages} nowrapColumns={['stateTransitionAt']}>
    <TextField source="id" label="ID" />
    <TextField source="state" label="Status" emptyText="—" />
    <DateField source="stateTransitionAt" label="Date" showTime emptyText="—" />
    <FunctionField<Ypackage> label="Carrier" render={pkg => (pkg.carrier ? `${pkg.carrier}${pkg.carrierService ? ` ${pkg.carrierService}` : ''}` : '—')} />
    <FunctionField<Ypackage> label="Dimensions" render={pkg => dims(pkg) ?? '—'} />
    <FunctionField<Ypackage> label="Weight" render={pkg => (pkg.weight != null ? `${pkg.weight} lbs` : '—')} />
    <FunctionField<Ypackage> label="Cost" render={pkg => formatMoney(isReturn ? pkg.inboundCost : pkg.actualShippingCost, currency)} />
    <FunctionField<Ypackage> label="Tracking" render={pkg => trackingCell(pkg)} />
  </ActivityGrid>
);

export const OrderActivityTabs = ({ order }: { order: OrderRecord }) => {
  const currency = order.transactionCurrencyCode;

  // Resolve each list once. Deriving the tab count from the raw list while the grid renders the
  // present() one makes the badge disagree with the rows it labels — "Packages (3)" over two —
  // and a list of only nulls shows a (1) tab over the empty state instead of being hidden.
  const history = present(order.stateTransitions);
  const packages = present(order.ypackages);
  const returnPackages = present(order.returnYpackages);
  const escalations = present(order.escalations);
  const emails = present(order.orderEmailHistory);

  const tabs: ActivityTab[] = [
    {
      key: 'history',
      label: 'History',
      count: history.length,
      render: () => (
        <ActivityGrid rows={history} nowrapColumns={['createdAt']}>
          <DateField source="createdAt" label="Timestamp" showTime emptyText="—" />
          <TextField source="changeType" label="Type" emptyText="—" />
          <TextField source="changeFrom" label="From" emptyText="—" />
          <TextField source="changeTo" label="To" emptyText="—" />
          <TextField source="reason" label="Reason" emptyText="—" />
          <TextField source="note" label="Notes" emptyText="—" />
          <TextField source="user.name" label="User" emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'packages',
      label: 'Packages',
      count: packages.length,
      render: () => <PackageGrid packages={packages} currency={currency} isReturn={false} />,
    },
    {
      key: 'returns',
      label: 'Return Packages',
      count: returnPackages.length,
      render: () => <PackageGrid packages={returnPackages} currency={currency} isReturn />,
    },
    {
      key: 'escalations',
      label: 'Escalations',
      count: escalations.length,
      render: () => (
        <ActivityGrid rows={escalations} nowrapColumns={['createdAt', 'closedAt']}>
          <DateField source="createdAt" label="Created" showTime emptyText="—" />
          <FunctionField<Escalation> label="Status" render={e => (e.deletedAt ? 'Deleted' : e.closedAt ? 'Closed' : 'Open')} />
          <TextField source="triggerTypeDisplay" label="Trigger" emptyText="—" />
          <TextField source="shipmentTypeDisplay" label="Shipment Type" emptyText="—" />
          <TextField source="reasonDisplay" label="Reason" emptyText="—" />
          <TextField source="closedReason" label="Closed Reason" emptyText="—" />
          <DateField source="closedAt" label="Closed At" showTime emptyText="—" />
        </ActivityGrid>
      ),
    },
    {
      key: 'emails',
      label: 'Emails',
      count: emails.length,
      render: () => (
        <ActivityGrid rows={emails} nowrapColumns={['date']}>
          <DateField source="date" label="Date" showTime emptyText="—" />
          <FunctionField<OrderEmail> label="Type" render={email => email.displayName ?? email.emailType ?? '—'} />
          <TextField source="toEmail" label="To" emptyText="—" />
          <TextField source="senderName" label="Sender" emptyText="—" />
          <TextField source="state" label="Status" emptyText="—" />
          <FunctionField<OrderEmail> label="Subject" render={email => (email.txUrl ? <Link href={email.txUrl} target="_blank" rel="noopener">{email.subjectLine}</Link> : email.subjectLine ?? '—')} />
        </ActivityGrid>
      ),
    },
  ];

  return <ActivityTabs tabs={tabs} />;
};

export default OrderActivityTabs;
